// Intent-matched landing pages: long-tail, high-commercial-intent searches
// mapped to the service we already deliver. Each page answers one specific
// question and routes the visitor to a booking or enquiry.

export type IntentPage = {
  slug: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intent: string;
  intro: string;
  deliverables: string[];
  turnaround: string;
  priceHint: string;
  faqs: { q: string; a: string }[];
  serviceSlug: string;
  cta: { label: string; to: string };
};

export const INTENT_PAGES: IntentPage[] = [
  {
    slug: "contract-drafting-johannesburg",
    h1: "Contract drafting in Johannesburg",
    metaTitle: "Contract Drafting Johannesburg | Fixed-Fee Legal Drafting",
    metaDescription:
      "Need a contract drafted in Johannesburg? Fixed-fee drafting and review of service, lease, employment and supplier agreements. Draft back in 2–3 working days.",
    intent: "Businesses and individuals in Gauteng searching for a drafter for a specific agreement.",
    intro:
      "We draft and review commercial agreements for clients across Johannesburg and the wider Gauteng region — remotely, with a clear scope and a fixed fee agreed before we start.",
    deliverables: [
      "A plain-language drafting brief confirming what the agreement must achieve",
      "First draft of the agreement, clause-numbered and ready to sign",
      "One round of revisions after your comments",
      "A short legal opinion on the risk clauses (non-reserved matters)",
    ],
    turnaround: "First draft in 2–3 working days; urgent turnaround on request.",
    priceHint: "From R950 for a standard agreement; quoted upfront after the brief.",
    faqs: [
      {
        q: "Do I need to come to an office in Johannesburg?",
        a: "No. Everything is handled by email, WhatsApp and video call, so we serve Johannesburg, Pretoria, Ekurhuleni and the rest of Gauteng without travel costs.",
      },
      {
        q: "Can you represent me in court if the contract is disputed?",
        a: "No. We provide legal information and legal advice on non-reserved matters and prepare legal opinions. Court appearance and litigation are reserved work, and we refer you to an admitted legal practitioner where a matter requires it.",
      },
    ],
    serviceSlug: "legal-drafting",
    cta: { label: "Get a drafting quote", to: "/contact" },
  },
  {
    slug: "contract-drafting-cape-town",
    h1: "Contract drafting in Cape Town",
    metaTitle: "Contract Drafting Cape Town | Fixed-Fee Agreements",
    metaDescription:
      "Fixed-fee contract drafting and review for Cape Town businesses: service, lease, employment, NDA and supplier agreements. First draft in 2–3 working days.",
    intent: "Western Cape businesses looking for affordable drafting without a law firm retainer.",
    intro:
      "We draft, review and tighten commercial agreements for Cape Town businesses and start-ups. Work is delivered remotely on a fixed fee, with the scope agreed before any drafting begins.",
    deliverables: [
      "Scoping call and written drafting brief",
      "Signature-ready first draft with numbered clauses",
      "One revision round and a plain-language clause guide",
      "Risk notes on payment, termination and liability clauses",
    ],
    turnaround: "First draft in 2–3 working days.",
    priceHint: "From R950 per agreement, quoted before we start.",
    faqs: [
      {
        q: "Do you work with Cape Town clients remotely?",
        a: "Yes. All drafting is delivered remotely by email and video call, so location does not affect the fee or the turnaround.",
      },
      {
        q: "Can you review a contract someone else drafted?",
        a: "Yes. A review gives you a marked-up version, a risk summary and suggested replacement wording for the clauses that expose you.",
      },
    ],
    serviceSlug: "legal-drafting",
    cta: { label: "Request a contract review", to: "/contact" },
  },
  {
    slug: "ccma-referral-help",
    h1: "Help with a CCMA referral",
    metaTitle: "CCMA Referral Help | Forms, Evidence & Preparation Support",
    metaDescription:
      "Support with CCMA referrals: LRA 7.11 forms, timelines, evidence bundles and conciliation preparation for employees and employers in South Africa.",
    intent: "Employees and employers who need to refer or defend a dispute at the CCMA.",
    intro:
      "We help you prepare a CCMA referral properly the first time — the correct form, the correct dates, and an evidence bundle that tells your story clearly at conciliation and arbitration.",
    deliverables: [
      "Completed LRA 7.11 referral form checked against the timelines",
      "Chronology of events and an indexed evidence bundle",
      "A written statement of case in your own words",
      "Preparation session covering the questions you will be asked",
    ],
    turnaround: "Referral pack within 48 hours where the deadline is tight.",
    priceHint: "From R750 for a referral pack; preparation sessions quoted separately.",
    faqs: [
      {
        q: "How long do I have to refer an unfair dismissal?",
        a: "Unfair dismissal disputes must generally be referred within 30 days of the date of dismissal. Unfair labour practice disputes generally have 90 days. Late referrals need a condonation application, which we can help you prepare.",
      },
      {
        q: "Will you appear at the CCMA for me?",
        a: "No. We prepare you and your documents. Representation at the CCMA hearing itself is handled by you, your union or an admitted legal practitioner where the rules allow it.",
      },
    ],
    serviceSlug: "ccma-support",
    cta: { label: "Book a CCMA preparation session", to: "/book" },
  },
  {
    slug: "popia-compliance-small-business",
    h1: "POPIA compliance for small businesses",
    metaTitle: "POPIA Compliance for Small Business | Practical Starter Pack",
    metaDescription:
      "A practical POPIA compliance pack for South African small businesses: privacy notice, consent wording, data register, breach process and staff briefing.",
    intent: "Owner-managed businesses that need to be POPIA compliant without a corporate budget.",
    intro:
      "POPIA applies to every business that handles customer or staff information — not only large companies. We deliver a right-sized compliance pack you can actually maintain.",
    deliverables: [
      "Website privacy notice and consent wording",
      "Record of processing activities (data register)",
      "Information Officer registration guidance",
      "Data breach response process and staff briefing note",
    ],
    turnaround: "Full starter pack in 5 working days.",
    priceHint: "From R2 500 for the small-business pack.",
    faqs: [
      {
        q: "Does POPIA apply to a business with only two employees?",
        a: "Yes. POPIA applies to any responsible party that processes personal information in South Africa, regardless of size. The obligations are scaled to the risk of the processing.",
      },
      {
        q: "Do I have to register an Information Officer?",
        a: "The head of the business is automatically the Information Officer and should be registered with the Information Regulator. We guide you through the registration.",
      },
    ],
    serviceSlug: "compliance",
    cta: { label: "Start your POPIA pack", to: "/contact" },
  },
  {
    slug: "employment-contract-review",
    h1: "Employment contract review",
    metaTitle: "Employment Contract Review South Africa | BCEA Check",
    metaDescription:
      "Have your employment contract reviewed against the BCEA and LRA. Get a marked-up copy, a risk summary and replacement wording within 2 working days.",
    intent: "Employees and employers who want a contract checked before signing.",
    intro:
      "Before you sign, know exactly what the contract does. We check your employment contract against the BCEA and LRA and explain each clause in plain language.",
    deliverables: [
      "Marked-up copy of the contract with comments",
      "Risk summary: restraint, notice, deductions, probation and overtime",
      "Suggested replacement wording for unfair or unlawful clauses",
      "A short written opinion you can send to the other side",
    ],
    turnaround: "Reviewed and returned within 2 working days.",
    priceHint: "From R650 per contract.",
    faqs: [
      {
        q: "Is a restraint of trade enforceable in South Africa?",
        a: "A restraint is enforceable if it is reasonable in scope, area and duration and protects a legitimate business interest. We flag restraints that are likely to be over-broad and suggest narrower wording.",
      },
      {
        q: "Can my employer deduct money from my salary?",
        a: "Only in the limited circumstances allowed by section 34 of the BCEA, usually with written consent for a specific debt. We check whether the deduction clause complies.",
      },
    ],
    serviceSlug: "labour-relations",
    cta: { label: "Send your contract for review", to: "/contact" },
  },
  {
    slug: "letter-of-demand",
    h1: "Letter of demand drafting",
    metaTitle: "Letter of Demand South Africa | Drafted in 24 Hours",
    metaDescription:
      "A properly drafted letter of demand for unpaid invoices, deposits or damages. Delivered within 24 hours with the correct legal wording and payment deadline.",
    intent: "People and businesses chasing money owed who need a formal demand first.",
    intro:
      "A clear, correctly worded letter of demand often recovers the money without any further step. We draft it, you send it — or we send it on your instruction.",
    deliverables: [
      "Letter of demand on our letterhead or yours",
      "Correct payment deadline and interest wording",
      "A record of delivery for later proceedings",
      "Guidance on the next step if the deadline passes",
    ],
    turnaround: "Drafted within 24 hours of receiving your documents.",
    priceHint: "From R450 per letter.",
    faqs: [
      {
        q: "How long should I give the other party to pay?",
        a: "Seven to fourteen business days is standard for an ordinary commercial debt, unless a contract or statute prescribes a specific period.",
      },
      {
        q: "What happens if they ignore the letter?",
        a: "The next step is usually summons or a small claims court application, depending on the amount. Litigation is reserved work, so we refer you to an admitted legal practitioner at that point.",
      },
    ],
    serviceSlug: "debt-assistance",
    cta: { label: "Request a letter of demand", to: "/contact" },
  },
];

export const getIntentPage = (slug: string) =>
  INTENT_PAGES.find((p) => p.slug === slug);
